import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Button, Typography, Divider } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import { IProduct } from '../../../../../interfaces/product';
import { IUser } from '../../../../../interfaces/user';
import { getProductImage } from '../../utils/getProductImage';
import { toAbsoluteUrl } from '../../../../../../_base';
import { useDefineUserRole, useFormatMessage } from '../../../../../hooks';
import { getMaxQuantityArray, thousands } from '../../../../../utils/utils';
import { ProductOffer } from '../ProductOffer';

export default function ProductTableItem({
  isAuthorized,
  me,
  product,
  prices,
}: {
  isAuthorized: boolean;
  me: IUser | undefined;
  product: IProduct;
  prices?: {
    price: number;
    amount: number;
  }[];
}) {
  const fm = useFormatMessage();
  const navigate = useNavigate();
  const { classes } = useStyles();
  const [productId, setProductId] = React.useState<number | null>(null);
  const isBuyer = useDefineUserRole(me, ['ROLE_BUYER', 'ROLE_BUYER_STAFF']);
  const priceValue =
    product.prices_values?.map(i => ({
      quantity_level: i.vendor?.quantity_level,
      second_quantity_level: i.vendor?.second_quantity_level,
    })) || [];
  const maxQuantite = getMaxQuantityArray(priceValue);
  const quantity = (product.stock_quantity || 0) + (product.order_quantity || 0);

  return (
    <>
      <div className={classes.row}>
        <div className={classes.leftCol}>
          <div className={classes.firstColumnItem}>
            <div className={classes.imgContainer}>
              <RouterLink to={`/viewproduct/${product.id}`}>
                <img
                  src={getProductImage(product)}
                  className={classes.img}
                  alt=''
                  onError={(e: any) => {
                    e.target.src = toAbsoluteUrl('/images/placeholder.png');
                  }}
                />
              </RouterLink>
            </div>
          </div>

          <div className={classes.columnItem}>
            <div>
              <div className={classes.productNameContainer}>
                <p className={classes.productName}>{product.sku || ''}</p>
              </div>
              {product.producer_name || product.manufacturer?.name ? (
                <span className={classes.productCompany}>
                  {product.producer_name || product.manufacturer?.name}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <Divider orientation='vertical' flexItem className={classes.divider} />

        <div className={classes.middleColumnItem}>
          <div>
            <Typography
              color='inherit'
              component='p'
              className={classes.productTitle}
              onClick={() => navigate(`/viewproduct/${product.id}`)}
            >
              {product.name}
            </Typography>
            <div className={classes.productDetails}>
              {product.package ? (
                <Typography
                  color='inherit'
                  component='span'
                  className={classes.productDetailText}
                >
                  {product.package}
                </Typography>
              ) : null}
              {product.promelec_params?.packaging ? (
                <Typography
                  color='inherit'
                  component='span'
                  className={classes.productDetailText}
                >
                  {product.promelec_params?.packaging}
                </Typography>
              ) : null}
            </div>
          </div>
        </div>

        <div className={classes.lastColumnItem}>
          <div className={classes.pricesContainer}>
            <div className={classes.price}>
              <Typography color='inherit' component='p' className={classes.priceValue}>
                Доступно для заказа:
              </Typography>
              <Typography color='inherit' component='p' className={classes.priceValue}>
                {quantity ? thousands(String(quantity)) : maxQuantite || '-'}
              </Typography>
            </div>
            {!prices && (
              <div className={classes.price}>
                <Typography color='inherit' component='p' className={classes.priceValue}>
                  Цена
                </Typography>
                <Typography color='inherit' component='p' className={classes.priceValue}>
                  от{' '}
                  {thousands(
                    String(
                      product.prices_values && product.prices_values.length > 0
                        ? Math.min(
                            ...(product.prices_values?.map(i => i.vendor?.min_price || 0) ||
                              [])
                          )
                        : '-'
                    )
                  )}{' '}
                  <span className={classes.rub}>RUB</span>
                </Typography>
              </div>
            )}
          </div>
          {prices && (
            <div className={classes.pricesContainerVendor} style={{}}>
              {prices?.slice(0, 3).map((i, index) => (
                <div className={classes.price} key={index}>
                  <Typography color='inherit' component='p' className={classes.priceValue}>
                    {thousands(String(i.amount || '-'))}
                  </Typography>
                  <Typography color='inherit' component='p' className={classes.priceValue}>
                    {thousands(String(i.price || '-'))}{' '}
                    <span className={classes.rub}>RUB</span>
                  </Typography>
                </div>
              ))}
              {prices?.slice(3).map((i, index) => (
                <div className={classes.price} key={index}>
                  <Typography color='inherit' component='p' className={classes.priceValue}>
                    {thousands(String(i.amount || '-'))}+
                  </Typography>
                  <Typography color='inherit' component='p' className={classes.priceValue}>
                    {thousands(String(i.price || '-'))}{' '}
                    <span className={classes.rub}>RUB</span>
                  </Typography>
                </div>
              ))}
            </div>
          )}
          <Button
            variant='contained'
            className={`${classes.edit} ${
              !isBuyer && isAuthorized ? classes.editSmallAdmin : classes.editSmall
            }`}
            onClick={e => {
              e.preventDefault();
              product.id && setProductId(product.id);
            }}
          >
            {fm(isBuyer || !isAuthorized ? 'BUY' : 'edit')}
          </Button>{' '}
        </div>
      </div>

      <ProductOffer id={productId} closeModal={() => setProductId(null)} />
    </>
  );
}

const useStyles = makeStyles()(theme => {
  return {
    img: {
      // maxHeight: 55,
      objectFit: 'contain',
      // width: 110,
      height: 100,
      [theme.breakpoints.down('md')]: {
        height: 50,
      },
      borderRadius: 16,
    },
    productNameContainer: {
      display: 'flex',
      flexDirection: 'column',
      textDecoration: 'none',
    },
    productName: {
      fontFamily: 'Montserrat !important',
      fontSize: 14,
      fontWeight: 'normal',
      color: '#0d0a05',
      marginBottom: 6,

      [theme.breakpoints.down('md')]: {
        fontSize: 13,
      },
    },
    productCompany: {
      fontSize: 14,
      fontWeight: theme.typography.fontWeightRegular,
      color: '#7c97c0',
      backgroundColor: '#fff3df',
      padding: '5px 10px',
      textDecoration: 'none',
      borderRadius: 4,

      [theme.breakpoints.down('md')]: {
        fontSize: 12,
      },
    },
    priceValue: {
      color: '#181c32',
      fontSize: '12px',
      fontWeight: theme.typography.fontWeightRegular,
      textWrap: 'nowrap',
      [theme.breakpoints.down('md')]: {
        margin: 0,
        fontSize: '11px',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '10px',
        textWrap: 'wrap',
      },
    },
    pricesContainer: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 223,
      padding: '11px 11.2px 12px 10.6px',
      borderRadius: 4,
      backgroundColor: '#a3a7b819',
      margin: '5px 12px',
      gap: 12,

      [theme.breakpoints.down('lg')]: {
        minWidth: 180,
        padding: '10px',
        marginLeft: 10,
        gap: 8,
      },

      [theme.breakpoints.down('md')]: {
        padding: '12px 10px',
        margin: '0px',
        gap: 8,
        width: '100%',
        marginLeft: 10,
        minWidth: 100,
      },
      [theme.breakpoints.down('sm')]: {
        gap: 4,
        padding: '8px 6px',
        marginLeft: 0,
      },
    },
    pricesContainerVendor: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      minWidth: 223,
      padding: '11px 11.2px 12px 10.6px',
      borderRadius: 4,
      backgroundColor: '#a3a7b819',
      margin: '5px 12px',
      gap: 12,
      [theme.breakpoints.down('md')]: {
        padding: '12px 10px',
        margin: '0px',
        gap: 8,
        width: '100%',
        minWidth: 100,
        gridTemplateColumns: '1fr 1fr',
      },
      [theme.breakpoints.down('sm')]: {
        gap: 4,
        padding: '8px 6px',
        gridTemplateColumns: '1fr',
      },
    },
    price: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: 30,
      [theme.breakpoints.down('md')]: {
        gap: 8,
      },
      [theme.breakpoints.down('sm')]: {
        gap: 4,
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#fff',
      borderRadius: 16,
      padding: 0,
      paddingRight: 22,
      textDecoration: 'none',
      color: 'inherit',
      width: '100%',
      boxSizing: 'border-box',

      [theme.breakpoints.down('lg')]: {
        paddingRight: 15,
      },

      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        paddingRight: 15,
        position: 'relative',
      },
    },
    firstColumnItem: {
      backgroundColor: '#fff',
      borderRadius: '16px 0 0 16px',
      borderBottom: 'none !important',
      padding: '8px 40px 8px 8px ',
      overflow: 'hidden',
      position: 'relative',

      [theme.breakpoints.down('md')]: {
        padding: '0px ',
      },
    },
    columnItem: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#fff',
      maxWidth: '100%',
      boxSizing: 'border-box',

      [theme.breakpoints.down('lg')]: {
        maxWidth: 200,
      },

      [theme.breakpoints.down('md')]: {
        maxWidth: 230,
      },
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%',
      },
    },
    middleColumnItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'center',
      backgroundColor: '#fff',
      padding: '0 20px',
      flex: 1,
      minWidth: 200,
      maxWidth: '100%',
      boxSizing: 'border-box',

      [theme.breakpoints.down('lg')]: {
        padding: '0 15px',
        minWidth: 150,
      },

      [theme.breakpoints.down('md')]: {
        padding: '10px 16px',
        width: '100%',
        marginLeft: 0,
        marginTop: 10,
        order: 2,
        clear: 'both',
        minWidth: 'auto',
      },
    },
    productTitle: {
      fontFamily: 'Montserrat !important',
      fontSize: 14,
      fontWeight: theme.typography.fontWeightRegular,
      color: '#0d0a05',
      marginBottom: 6,
      wordBreak: 'break-word',
      cursor: 'pointer',
      lineHeight: 1.2,
      [theme.breakpoints.down('md')]: {
        fontSize: 13,
        marginBottom: 6,
      },
    },
    productDetails: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flexWrap: 'wrap',

      [theme.breakpoints.down('md')]: {
        gap: 4,
      },
      [theme.breakpoints.down('sm')]: {
        gap: 2,
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    productDetailText: {
      fontSize: 14,
      fontWeight: theme.typography.fontWeightRegular,
      color: '#000',
      backgroundColor: '#f6f6f8',
      borderRadius: 4,
      padding: '5px 10px',
      lineHeight: 1,
      minHeight: 26,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',

      [theme.breakpoints.down('md')]: {
        fontSize: 11,
      },
    },

    lastColumnItem: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#fff',
      borderRadius: '0 16px 16px 0',
      margin: '8px 0',
      flexShrink: 0,
      [theme.breakpoints.down('lg')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
      [theme.breakpoints.down('md')]: {
        order: 3,
        width: '100%',
        marginLeft: 0,
        marginTop: 10,
        borderRadius: 16,
      },
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'row',
        marginLeft: 16,
        justifyContent: 'space-between',
        width: '95%',
        alignItems: 'flex-start',
      },
    },
    edit: {
      margin: 12,
      textTransform: 'inherit',
      height: 48,
      fontSize: 17,
      fontWeight: theme.typography.fontWeightRegular,
      color: '#fff',

      [theme.breakpoints.down('sm')]: {
        margin: '0 0 0 15px',
        height: 60,
        // fontSize: 12,
      },
    },
    editSmall: {
      fontSize: 17,
      [theme.breakpoints.down('sm')]: {
        fontSize: 12,
      },
    },
    editSmallAdmin: {
      fontSize: 17,
      [theme.breakpoints.down('sm')]: {
        fontSize: 8,
      },
    },
    rub: {
      color: '#7c97c0',
    },
    leftCol: {
      display: 'flex',
      flexDirection: 'row',
      flexShrink: 0,
      // minWidth: 310,
      paddingRight: 20,
      [theme.breakpoints.down('lg')]: {
        minWidth: 250,
      },
      [theme.breakpoints.down('md')]: {
        width: '100%',
        order: 1,
        minWidth: 'auto',
      },
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'row',
      },
    },
    imgContainer: {
      width: 110,
      height: 110,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      [theme.breakpoints.down('sm')]: {
        justifyContent: 'flex-start',
        marginLeft: 16,
        width: 60,
        height: 60,
        marginTop: 10,
      },
    },
    divider: {
      height: 'auto',
      margin: '27px 8px',
      alignSelf: 'stretch',
      backgroundColor: '#7c97c0',
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
  };
});
